import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Minus, CalendarPlus } from 'lucide-react';
import { DASH, authHeader } from '../api';
import { SectionCard, Spinner, EmptyState, Badge, GhostButton, fmtDate } from '../ui';

const CreditsSection = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${DASH}/credits`, { headers: authHeader() })
      .then((res) => setRows(res.data || []))
      .catch(() => setError('Could not load credits.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const adjust = async (id, delta) => {
    try {
      await axios.post(`${DASH}/credits/${id}/adjust`, { delta, reason: 'admin adjustment' }, { headers: authHeader() });
      load();
    } catch {
      setError('Could not adjust credit.');
    }
  };

  const extend = async (id) => {
    try {
      await axios.post(`${DASH}/credits/${id}/extend`, { days: 30, reason: 'admin extension' }, { headers: authHeader() });
      load();
    } catch {
      setError('Could not extend expiry.');
    }
  };

  if (loading) return <Spinner label="Loading credits…" />;

  return (
    <div data-testid="wellness-admin-section-credits" className="space-y-4">
      <div>
        <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>
          Credit Control
        </h2>
        <p className="text-sm text-[#5a7062] mt-1">All active packs &amp; memberships with sessions remaining.</p>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <SectionCard testId="credits-card">
        {rows.length === 0 ? (
          <EmptyState testId="credits-empty" label="No active credits right now." />
        ) : (
          <div className="space-y-3">
            {rows.map((c) => (
              <div key={c.id} data-testid={`credit-row-${c.id}`} className="flex items-center justify-between gap-4 border border-[#f0ebe0] rounded-xl p-4">
                <div>
                  <div className="font-semibold text-[#0d1f15]">{c.first_name} <span className="text-xs text-[#5a7062] font-normal ml-1">{c.email}</span></div>
                  <div className="text-xs text-[#5a7062] mt-0.5">Expires {fmtDate(c.expires_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge tone={c.type === 'membership' ? 'gold' : 'green'}>{c.type} · {c.sessions_remaining} left</Badge>
                  <div className="flex items-center gap-1">
                    <GhostButton testId={`credit-dec-${c.id}`} onClick={() => adjust(c.id, -1)}><Minus size={13} /></GhostButton>
                    <GhostButton testId={`credit-inc-${c.id}`} onClick={() => adjust(c.id, 1)}><Plus size={13} /></GhostButton>
                  </div>
                  <GhostButton testId={`credit-extend-${c.id}`} onClick={() => extend(c.id)}><CalendarPlus size={13} /> +30d</GhostButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default CreditsSection;
